import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, Pagination, Stack } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";

import PageContainer from "../layouts/PageContainer";
import PageToolbar from "../components/common/PageToolbar";
import AppButton from "../components/buttons/AppButton";
import ConfirmDialog from "../components/common/ConfirmDialog";
import CategoryCard from "../components/categories/CategoryCard";
import CreateCategoryModal from "../components/categories/CreateCategoryModal";
import UpdateCategoryModal from "../components/categories/UpdateCategoryModal";
import AppCircularProgress from "../components/common/AppCircularProgress";
import PageError from "../components/common/PageError";

import useDebounce from "../hooks/useDebounce";
import { usePermissions } from "../hooks/usePermissions";
import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  getCategories,
  deleteCategory,
  resetCategoryServerStatus,
} from "../redux/actions/categoryActions";
import { CategoryResponseDto } from "../shared/dtos/category/CategoryResponseDto";

const CategoriesPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();

  const {
    items: categories,
    pagination,
    loadingFetchAll: loadingCategories,
    errorFetchAll: errorLoadingCategories,
    serverStatus,
    //loadingDelete: loadingCategoryDelete,
    loadingUpdate: loadingCategoryUpdate,
    loadingCreate: loadingCategoryCreate,
    errorCreate: errorCreatingCategory,
    errorUpdate: errorUpdatingCategory,
  } = useAppSelector((state) => state.category);

  const [page, setPage] = useState<number>(1);
  const limit = 12;
  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearch = useDebounce<string>(searchTerm, 500);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [selectedCategory, setSelectedCategory] =
    useState<CategoryResponseDto | null>(null);

  const canCreate = hasPermission("category:create");
  const canUpdate = hasPermission("category:update");
  const canDelete = hasPermission("category:delete");

  const fetchCategories = useCallback(() => {
    dispatch(getCategories({ page, size: limit, search: debouncedSearch }));
  }, [dispatch, page, limit, debouncedSearch]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    if (!serverStatus) return;
    const { operation, status } = serverStatus;
    if (operation === "create" && (status === 201 || status === 200))
      setIsCreateModalOpen(false);
    if (operation === "update" && status === 200) setIsUpdateModalOpen(false);
    if (operation === "delete" && status === 200) setIsDeleteModalOpen(false);
    dispatch(resetCategoryServerStatus());
  }, [serverStatus, dispatch]);

  const renderContent = (): React.JSX.Element => {
    if (loadingCategories) return <AppCircularProgress />;
    if (errorLoadingCategories)
      return (
        <PageError error={errorLoadingCategories} onRetry={fetchCategories} />
      );

    if (!categories || categories.length === 0)
      return (
        <Box sx={{ textAlign: "center", mt: 5 }}>
          <Typography>No Categories Found</Typography>
        </Box>
      );

    return (
      <>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
              lg: "repeat(4, 1fr)",
            },
            gap: 3,
          }}
        >
          {categories.map((category) => (
            <CategoryCard
              key={category.id}
              category={category as unknown as CategoryResponseDto}
              onEdit={
                canUpdate
                  ? () => {
                      setSelectedCategory(
                        category as unknown as CategoryResponseDto,
                      );
                      setIsUpdateModalOpen(true);
                    }
                  : undefined
              }
              onDelete={
                canDelete
                  ? () => {
                      setSelectedCategory(
                        category as unknown as CategoryResponseDto,
                      );
                      setIsDeleteModalOpen(true);
                    }
                  : undefined
              }
            />
          ))}
        </Box>

        {pagination?.totalPages && pagination.totalPages > 1 && (
          <Stack sx={{ alignItems: "center", mt: 4 }}>
            <Pagination
              count={pagination.totalPages}
              page={page}
              onChange={(_, v) => setPage(v)}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Stack>
        )}
      </>
    );
  };

  return (
    <PageContainer
      title="Categories"
      subtitle="Organize your inventory and services into manageable groupings."
      actions={
        canCreate && (
          <AppButton
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => setIsCreateModalOpen(true)}
          >
            Add Category
          </AppButton>
        )
      }
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        searchPlaceholder="Search categories..."
      />
      {renderContent()}

      {isCreateModalOpen && (
        <CreateCategoryModal
          open={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          loading={loadingCategoryCreate}
          error={errorCreatingCategory}
        />
      )}
      {isUpdateModalOpen && selectedCategory && (
        <UpdateCategoryModal
          open={isUpdateModalOpen}
          onClose={() => setIsUpdateModalOpen(false)}
          category={selectedCategory}
          loading={loadingCategoryUpdate}
          error={errorUpdatingCategory}
        />
      )}
      {isDeleteModalOpen && selectedCategory && (
        <ConfirmDialog
          open={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onConfirm={() => dispatch(deleteCategory(selectedCategory.id))}
          title="Delete Category"
          description={`Are you sure you want to delete "${selectedCategory.name}"?`}
          // loading={loadingCategoryDelete}
        />
      )}
    </PageContainer>
  );
};

export default CategoriesPage;
